import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { InstagramIcon, MailIcon, FacebookIcon, YoutubeIcon, LockIcon, EyeIcon, EyeOffIcon, XIcon, Gamepad2Icon } from 'lucide-react';
import { GlitterHeart } from './GlitterHeart';
const GAME_PASSWORD = 'Perditashits@2080';
const GAME_PATH = '/games/ttl-483';
function GameLockModal({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === GAME_PASSWORD) {
      sessionStorage.setItem('twm-game-auth', 'true');
      setError(false);
      setPassword('');
      onClose();
      navigate(GAME_PATH);
    } else {
      setError(true);
      setPassword('');
    }
  };
  return <AnimatePresence data-id="element-102">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose} data-id="element-103">
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl border border-plum-100 p-8 w-full max-w-sm relative" data-id="element-104">
            <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full text-plum-400 hover:text-plum-700 hover:bg-plum-50 transition-colors" aria-label="Close" data-id="element-105">
              <XIcon className="w-5 h-5" data-id="element-106" />
            </button>

            <div className="text-center" data-id="element-107">
              <div className="flex justify-center mb-3" data-id="element-108">
                <GlitterHeart size={32} data-id="element-109" />
              </div>
              <div className="w-12 h-12 rounded-full bg-fuchsia-100 flex items-center justify-center mx-auto mb-4" data-id="element-110">
                <LockIcon className="w-6 h-6 text-fuchsia-600" data-id="element-111" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-plum-800 mb-2" data-id="element-112">
                Trippin' Through Life
              </h2>
              <p className="text-plum-400 text-sm font-medium mb-6" data-id="element-113">
                Enter the password to play the game.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3" data-id="element-114">
                <div className="relative" data-id="element-115">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => {
                setPassword(e.target.value);
                setError(false);
              }} placeholder="Enter password" className={`w-full px-5 py-3 rounded-2xl border-2 font-medium text-plum-800 placeholder-plum-300 outline-none transition-all pr-12 ${error ? 'border-red-400 bg-red-50' : 'border-plum-200 bg-plum-50 focus:border-cta-400 focus:bg-white'}`} autoFocus data-id="element-116" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-plum-400 hover:text-plum-600 transition-colors" data-id="element-117">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-118" /> : <EyeIcon className="w-5 h-5" data-id="element-119" />}
                  </button>
                </div>

                {error && <p className="text-red-500 text-sm font-semibold" data-id="element-120">
                    Incorrect password. Try again.
                  </p>}

                <button type="submit" className="w-full py-3 rounded-2xl bg-cta-500 text-white font-bold text-base hover:bg-cta-600 transition-colors shadow-lg shadow-cta-500/20" data-id="element-121">
                  Let's Play 🎲
                </button>
              </form>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [showGameModal, setShowGameModal] = useState(false);
  const navigate = useNavigate();
  const handleGameClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (sessionStorage.getItem('twm-game-auth') === 'true') {
      navigate(GAME_PATH);
    } else {
      setShowGameModal(true);
    }
  };
  return <footer className="bg-white border-t border-blush-100 pt-16 pb-8 relative overflow-hidden" data-id="element-122">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-blush-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-float" data-id="element-123" />
      <div className="absolute top-0 right-1/4 w-64 h-64 bg-lavender-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-float" style={{
      animationDelay: '2s'
    }} data-id="element-124" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-125">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12" data-id="element-126">
          <div className="col-span-1 md:col-span-2" data-id="element-127">
            <Link to="/" className="flex items-center gap-2 mb-4 group" data-id="element-128">
              <GlitterHeart size={22} className="group-hover:scale-125 transition-transform" data-id="element-129" />
              <span className="font-heading text-2xl font-semibold text-plum-700" data-id="element-130">
                Trippin' With Mandy™
              </span>
            </Link>
            <p className="text-plum-500 max-w-sm mb-6 leading-relaxed" data-id="element-131">
              Rebuilding the world one soul at a time. The Broken Beginning —
              and taking you with me.
            </p>
            <div className="flex flex-wrap gap-3" data-id="element-132">
              <a href="https://www.facebook.com/share/1KSrTKNe17/" target="_blank" rel="noopener noreferrer" className="p-2 bg-blush-50 rounded-full text-plum-500 hover:text-cta-500 hover:bg-blush-100 transition-colors" aria-label="Facebook" data-id="element-133">
                <FacebookIcon className="w-5 h-5" data-id="element-134" />
              </a>
              <a href="https://www.instagram.com/mandy.lee.944023?igsh=eTVlMHFtbXM3M216" target="_blank" rel="noopener noreferrer" className="p-2 bg-blush-50 rounded-full text-plum-500 hover:text-cta-500 hover:bg-blush-100 transition-colors" aria-label="Instagram" data-id="element-135">
                <InstagramIcon className="w-5 h-5" data-id="element-136" />
              </a>
              <a href="https://www.tiktok.com/@trippin.with.mandy?_r=1&_t=ZP-94syxD66BF1" target="_blank" rel="noopener noreferrer" className="p-2 bg-blush-50 rounded-full text-plum-500 hover:text-cta-500 hover:bg-blush-100 transition-colors" aria-label="TikTok - Main" data-id="element-137">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-138">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-139" />
                </svg>
              </a>
              <a href="https://youtube.com/@trippinwithmandy?si=y8DXbx-brot4uKGM" target="_blank" rel="noopener noreferrer" className="p-2 bg-blush-50 rounded-full text-plum-500 hover:text-cta-500 hover:bg-blush-100 transition-colors" aria-label="YouTube" data-id="element-140">
                <YoutubeIcon className="w-5 h-5" data-id="element-141" />
              </a>
              <a href="mailto:trippinwithmandy@gmail.com" className="p-2 bg-blush-50 rounded-full text-plum-500 hover:text-cta-500 hover:bg-blush-100 transition-colors" aria-label="Email" data-id="element-142">
                <MailIcon className="w-5 h-5" data-id="element-143" />
              </a>
            </div>
          </div>

          <div data-id="element-144">
            <h3 className="font-heading font-semibold text-lg text-plum-700 mb-4" data-id="element-145">
              Explore
            </h3>
            <ul className="space-y-3" data-id="element-146">
              <li data-id="element-147">
                <Link to="/" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-148">
                  Home
                </Link>
              </li>
              <li data-id="element-149">
                <Link to="/about" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-150">
                  About Mandy
                </Link>
              </li>
              <li data-id="element-151">
                <Link to="/courses" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-152">
                  Courses
                </Link>
              </li>
              <li data-id="element-153">
                <Link to="/coaching" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-154">
                  1:1 Coaching
                </Link>
              </li>
              <li data-id="element-155">
                <Link to="/travel" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-156">
                  Travel Planning
                </Link>
              </li>
              <li data-id="element-157">
                <a href="https://beacons.ai/trippinwithmandy" target="_blank" rel="noopener noreferrer" className="text-cta-500 font-bold hover:text-cta-600 transition-colors inline-flex items-center gap-1" data-id="element-158">
                  Shop My Favorites <GlitterHeart size={12} data-id="element-159" />
                </a>
              </li>
            </ul>
          </div>

          <div data-id="element-160">
            <h3 className="font-heading font-semibold text-lg text-plum-700 mb-4" data-id="element-161">
              Resources
            </h3>
            <ul className="space-y-3" data-id="element-162">
              <li data-id="element-163">
                <Link to="/app" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-164">
                  The App (Coming Soon)
                </Link>
              </li>
              <li data-id="element-165">
                <Link to="/services/web-app-design" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-166">
                  Web & App Design
                </Link>
              </li>
              <li data-id="element-167">
                <Link to="/free-resources" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-168">
                  Free Resources
                </Link>
              </li>
              <li data-id="element-169">
                <a href="mailto:trippinwithmandy@gmail.com" className="text-plum-500 hover:text-plum-700 transition-colors" data-id="element-170">
                  Contact
                </a>
              </li>
              <li data-id="element-171">
                <button onClick={handleGameClick} className="text-plum-500 hover:text-fuchsia-600 transition-colors inline-flex items-center gap-1.5 group" data-id="element-172">
                  <Gamepad2Icon className="w-4 h-4 group-hover:scale-110 transition-transform" data-id="element-173" />
                  Trippin' Through Life Game
                  <LockIcon className="w-3 h-3 opacity-60" data-id="element-174" />
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-blush-100 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-175">
          <p className="text-sm text-plum-500" data-id="element-176">
            © {new Date().getFullYear()} Trippin' With Mandy™. All rights
            reserved.
          </p>
          <div className="flex items-center gap-4 text-sm text-plum-500" data-id="element-177">
            <Link to="/privacy" className="hover:text-plum-700 transition-colors font-medium" data-id="element-178">
              Privacy Policy
            </Link>
            <span className="text-plum-300" data-id="element-179">•</span>
            <Link to="/terms" className="hover:text-plum-700 transition-colors font-medium" data-id="element-180">
              Terms & Conditions
            </Link>
          </div>
          <p className="text-sm text-plum-500 flex items-center gap-1" data-id="element-181">
            Made with <GlitterHeart size={16} data-id="element-182" /> and a whole lot of grit
          </p>
        </div>
      </div>

      <GameLockModal open={showGameModal} onClose={() => setShowGameModal(false)} data-id="element-183" />
    </footer>;
}